/*
* === App.js (Final - With Coming Soon & Global Guards) ===
* Features:
* - Global "Coming Soon" Lock for non-SuperUsers
* - Pre-fetches System Settings (Logo, Maintenance Mode)
* - Prevents page flicker during loading
*/
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Box, Snackbar, Alert, CircularProgress, GlobalStyles } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CustomThemeProvider } from './context/ThemeContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import api from './services/api';
import { HelmetProvider } from 'react-helmet-async';

// Components
import Navbar from './components/Navbar';
import PrivateRoute from './components/PrivateRoute';
import Footer from './components/Footer';
import SessionHandler from './components/SessionHandler';
import MaintenancePage from './client/pages/MaintenancePage';
import UnauthorizedPage from './client/pages/UnauthorizedPage';
import ComingSoonPage from './client/pages/ComingSoonPage';
import HybridAptitudeGame from './components/guidance/HybridAptitudeGame'; 

// Pages
import HomePage from './client/pages/HomePage';
import AuthPage from './client/pages/AuthPage';
import ForgotPasswordPage from './client/pages/ForgotPasswordPage';
import ResetPasswordPage from './client/pages/ResetPasswordPage';
import PricingPage from './client/pages/public/PricingPage';
import BillingPolicyPage from './client/pages/public/BillingPolicyPage';
import SwitchApplicantPage from './client/pages/public/SwitchApplicantPage';

// Staff Dashboards
import AdminDashboardPage from './client/pages/AdminDashboard';
import { ReviewerDashboard, ApproverDashboard, FinanceDashboard } from './client/pages/StaffDashboards';

import SearchApplicationsPage from './client/pages/SearchApplicationsPage';
import ReportsPage from './client/pages/ReportsPage';
import ReviewApplicationPage from './client/pages/ReviewApplicationPage';
import TemplateManagerPage from './client/pages/TemplateManagerPage';
import ContractManagerPage from './client/pages/ContractManagerPage';
import UserManagementPage from './client/pages/UserManagementPage';
import SecurityHubPage from './client/pages/SecurityHubPage';
import ApplicantDashboardPage from './client/pages/ApplicantDashboardPage';
import ApplicationFormPage from './client/pages/ApplicationFormPage';
import ProfilePage from './client/pages/ProfilePage';
import MessageCenterPage from './client/pages/MessageCenterPage';
import MyDocumentsPage from './client/pages/MyDocumentsPage';
import SystemSettingsPage from './client/pages/SystemSettingsPage';
import AcademicProgressPage from './client/pages/AcademicProgressPage';
import CompanySubscriptionPage from './client/pages/CompanySubscriptionPage';
import ReviewsManagement from './client/pages/ReviewsManagementPage';
import BursaryManagementPage from './client/pages/BursaryManagementPage';
import FunderBillingPage from './client/pages/FunderBillingPage';
import EmailQueuePage from './client/pages/EmailQueuePage';

// Public Pages
import PrivacyPolicyPage from './client/pages/public/PrivacyPolicyPage';
import TermsOfServicePage from './client/pages/public/TermsOfServicePage';
import AboutUsPage from './client/pages/public/AboutUsPage';
import FAQPage from './client/pages/public/FAQPage';
import ContactPage from './client/pages/public/ContactPage';
import SitemapPage from './client/pages/public/SiteMapPage';
import VerifyFunderPage from './client/pages/VerifyFunderPage';
import BursaryDetailsPage from './client/pages/public/BursaryDetailsPage';

// Form Step Components
import PersonalDetailsStep from './components/application-steps/PersonalDetailsStep';
import AcademicInfoStep from './components/application-steps/AcademicInfoStep';
import FinancialInfoStep from './components/application-steps/FinancialInfoStep';
import DocumentUploadStep from './components/application-steps/DocumentUploadStep';
import ReviewStep from './components/application-steps/ReviewStep';
import GuardianDetailsStep from './components/application-steps/GuardianDetailsStep';
import AddressStep from './components/application-steps/AddressStep';
import SystemAlerts from './components/SystemAlertBanner';
import ErrorBoundary from './components/ErrorBoundary';

const GlobalToastHandler = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    const handleLogoutReason = (e) => {
      if (!localStorage.getItem('token')) return; 
      setMessage(e.detail);
      setOpen(true);
    };
    window.addEventListener('auth:logout-reason', handleLogoutReason);
    return () => window.removeEventListener('auth:logout-reason', handleLogoutReason);
  }, []);
  return (
    <Snackbar open={open} autoHideDuration={6000} onClose={() => setOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setOpen(false)} sx={{ width: '100%' }}>{message}</Alert>
    </Snackbar>
  );
};

// --- Main Content with Global Guards ---
const AppContent = () => {
    const { user, loading: authLoading } = useAuth();
    const { comingSoon, maintenance, loading: settingsLoading } = useSettings();
    const location = useLocation();

    if (authLoading || settingsLoading) {
        return (
            <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
                <CircularProgress />
            </Box>
        );
    }

    // 2. GLOBAL GUARD: Coming Soon Mode
    if (comingSoon) {
        const isSuperUser = user?.role === 'SuperUser';
        const isAuthRoute = location.pathname === '/login' || location.pathname === '/admin/login';

        if (!isSuperUser && !isAuthRoute) {
            return <ComingSoonPage />;
        }
    }

    // 3. GLOBAL GUARD: Maintenance Mode (Legacy)
    const isStaff = ['Admin', 'SuperUser'].includes(user?.role);
    const isLoginPage = location.pathname === '/login';

    if (maintenance.active && !isStaff && !isLoginPage) {
        return <MaintenancePage endTime={maintenance.endTime} showCountdown={maintenance.showTimer} />;
    }

    // 4. Render Main Application
    return (
        <HelmetProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
            <GlobalStyles styles={{ html: { margin: 0, padding: 0, height: '100%', width: '100%' }, body: { margin: 0, padding: 0, height: '100%', width: '100%', backgroundColor: '#121212' }, '#root': { height: '100%', width: '100%' } }} />
            
            <SystemAlerts />
            <ErrorBoundary>
                <Navbar />
            </ErrorBoundary>
            
            {user && <SessionHandler />}
            <GlobalToastHandler />
            
            <ErrorBoundary>
                <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pb: 8 }}>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                        <Route path="/" element={<HomePage />} />
                        <Route path="/login" element={<AuthPage />} />
                        <Route path="/register" element={<AuthPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/unauthorized" element={<UnauthorizedPage />} />
                        <Route path="/verify-funder" element={<VerifyFunderPage />} />
                        <Route path="/switch-applicant" element={<SwitchApplicantPage />} />
                        <Route path="/bursary/:id" element={<BursaryDetailsPage />} />
                        {/* FIX: Route to handle the /auth URL used by the buttons */}
                        <Route path="/auth" element={<AuthPage />} />
                        <Route path="/coming-soon" element={<ComingSoonPage />} />
                        <Route path="/pricing" element={<PricingPage />} /> 
                        
                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                        <Route path="/about-us" element={<AboutUsPage />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/sitemap" element={<SitemapPage />} />
                        <Route path="/billing-policy" element={<BillingPolicyPage />} />

                        {/* Authenticated Common */}
                        <Route path="/profile" element={<PrivateRoute roles={['Applicant', 'Admin', 'SuperUser', 'Reviewer', 'Finance', 'Approver']}><ProfilePage /></PrivateRoute>} />
                        <Route path="/messages" element={<PrivateRoute roles={['Applicant', 'Admin', 'SuperUser', 'Reviewer', 'Finance', 'Approver']}><MessageCenterPage /></PrivateRoute>} />

                        {/* Applicant */}
                        <Route path="/applicant/dashboard" element={<PrivateRoute roles={['Applicant']}><ApplicantDashboardPage /></PrivateRoute>} />
                        <Route path="/applicant/documents" element={<PrivateRoute roles={['Applicant']}><MyDocumentsPage /></PrivateRoute>} />
                        <Route path="/applicant/progress" element={<PrivateRoute roles={['Applicant']}><AcademicProgressPage /></PrivateRoute>} />
                        <Route path="/aptitude-game" element={<PrivateRoute roles={['Applicant', 'SuperUser', 'Admin', 'Reviewer', 'Finance', 'Approver']}><HybridAptitudeGame /></PrivateRoute>} />
                        
                        {/* Application Form Routes */}
                        <Route path="/applicant/form" element={<PrivateRoute roles={['Applicant']}><ApplicationFormPage /></PrivateRoute>}>
                            <Route index element={<PersonalDetailsStep />} />
                            <Route path="personal" element={<PersonalDetailsStep />} />
                            <Route path="guardian" element={<GuardianDetailsStep />} />
                            <Route path="address" element={<AddressStep />} />
                            <Route path="financial" element={<FinancialInfoStep />} />
                            <Route path="academic" element={<AcademicInfoStep />} />
                            <Route path="documents" element={<DocumentUploadStep />} />
                            <Route path="review" element={<ReviewStep />} />
                        </Route>

                        {/* Staff Dashboards */}
                        <Route path="/dashboard/reviewer" element={<PrivateRoute roles={['Reviewer', 'Admin', 'SuperUser']}><ReviewerDashboard /></PrivateRoute>} />
                        <Route path="/dashboard/approver" element={<PrivateRoute roles={['Approver', 'Admin', 'SuperUser']}><ApproverDashboard /></PrivateRoute>} />
                        <Route path="/dashboard/finance" element={<PrivateRoute roles={['Finance', 'Admin', 'SuperUser']}><FinanceDashboard /></PrivateRoute>} />
                        
                        {/* Funder / Billing Routes */}
                        <Route path="/funder/billing" element={<PrivateRoute roles={['Funder', 'Admin', 'SuperUser']}><FunderBillingPage /></PrivateRoute>} />

                        {/* Admin Routes */}
                        <Route path="/admin/search" element={<PrivateRoute roles={['Admin', 'SuperUser', 'Reviewer', 'Finance', 'Approver']}><SearchApplicationsPage /></PrivateRoute>} />
                        <Route path="/admin/review/:id" element={<PrivateRoute roles={['Admin', 'SuperUser', 'Reviewer', 'Finance', 'Approver']}><ReviewApplicationPage /></PrivateRoute>} />
                        
                        <Route path="/admin/dashboard" element={<PrivateRoute roles={['Admin', 'SuperUser']}><AdminDashboardPage /></PrivateRoute>} />
                        <Route path="/admin/reports" element={<PrivateRoute roles={['Admin', 'SuperUser', 'Finance']}><ReportsPage /></PrivateRoute>} />
                        <Route path="/admin/bursaries" element={<PrivateRoute roles={['Admin', 'SuperUser', 'Approver']}><BursaryManagementPage /></PrivateRoute>} />
                        <Route path="/admin/templates" element={<PrivateRoute roles={['Admin', 'SuperUser']}><TemplateManagerPage /></PrivateRoute>} />
                        <Route path="/admin/contracts" element={<PrivateRoute roles={['Admin', 'SuperUser']}><ContractManagerPage /></PrivateRoute>} />
                        <Route path="/admin/users" element={<PrivateRoute roles={['Admin', 'SuperUser']}><UserManagementPage /></PrivateRoute>} />
                        <Route path="/admin/queue" element={<PrivateRoute roles={['Admin', 'SuperUser']}><EmailQueuePage /></PrivateRoute>} />
                        
                        {/* SuperUser Only */}
                        <Route path="/admin/security-hub" element={<PrivateRoute roles={['Admin', 'SuperUser']}><SecurityHubPage /></PrivateRoute>} />
                        <Route path="/admin/settings" element={<PrivateRoute roles={['SuperUser']}><SystemSettingsPage /></PrivateRoute>} />
                        <Route path="/admin/reviews" element={ <PrivateRoute roles={['SuperUser']}><ReviewsManagement /></PrivateRoute> } />
                        <Route 
                            path="/admin/subscriptions" 
                            element={
                                <PrivateRoute roles={['SuperUser']}>
                                    <CompanySubscriptionPage />
                                </PrivateRoute>
                            } 
                        />
                    </Routes>
                </Box>
            </ErrorBoundary>
            <Footer />
        </Box>
        </HelmetProvider>
    );
};

function App() {
  return (
    <CustomThemeProvider>
        <CssBaseline />
            <SettingsProvider>
                <AuthProvider>
                    <AppContent />
                </AuthProvider>
            </SettingsProvider>
    </CustomThemeProvider>
  );
}

export default App;
